/**
 * Step 4 — the release's cover art.
 *
 * §11 asks for three routes to artwork: reuse what a song already has, upload a new
 * image, or generate one. All three are presented here, and the generate route now asks
 * the backend for a real image instead of showing a placeholder notice.
 *
 * ============================================================================
 * CHOOSING RELEASE ARTWORK NEVER WRITES TO THE SONG.
 *
 * §11: "do not silently overwrite the original song artwork unless the user explicitly
 * chooses to". The release carries its own `artworkUrl`/`artworkFromSongId`, and nothing
 * on this screen can reach `Song.coverUrl` — the reuse option stores the song's ID and
 * reads its artwork, which is a reference, not a copy and not a write. The Studio's Cover
 * Art tool remains the only place a song's own artwork changes.
 * ============================================================================
 *
 * NO INVENTED VALIDATION RULES. §11 again: the size and format checks are
 * lib/imageUpload's, which the Studio already uses, and there is deliberately no minimum
 * dimension check — the committed LabelGrid spec states no artwork dimension
 * requirements, so a "must be at least 3000×3000" rule here would be this app making one
 * up. A real deployment reads the requirement from LabelGrid and enforces it server-side.
 *
 * The upload target is a <label> wrapping a visually hidden file input, which is the
 * pattern CoverPreview uses and for the same reason: click, tap, Enter, Space and drop
 * all work with no keydown handler of our own. Buttons therefore sit OUTSIDE the label —
 * a button inside it would also open the picker.
 */
import { AlertTriangle, Image as ImageIcon, ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import { Btn } from '../ui';
import { RadioCard, RadioCardGroup, TextField } from '../forms';
import ReleaseArtwork from './ReleaseArtwork';
import { SongArtwork } from '../songs';
import {
  ACCEPTED_IMAGE_LABEL,
  ACCEPT_ATTRIBUTE,
  MAX_IMAGE_LABEL,
  firstImageFrom,
} from '../../lib/imageUpload';
import { c } from '../../styles/theme';
import type { PublisherRelease, Song } from '../../types/domain';

/** Where the release's artwork comes from. */
export type ArtworkSource = 'song' | 'upload' | 'generate';

export interface ArtworkStepProps {
  release: PublisherRelease;
  /** The songs ON THIS RELEASE — the only ones whose artwork it makes sense to reuse. */
  songs: Song[];
  source: ArtworkSource;
  onSourceChange: (source: ArtworkSource) => void;
  /** Records that the user chose to reuse this song's artwork. */
  onReuseSong: (songId: string) => void;
  /** Hands over a picked or dropped file. Validation and reading happen in the route. */
  onUpload: (file: File) => void;
  /** Writes the draft prompt for generated artwork. */
  onArtworkPromptChange: (prompt: string) => void;
  /** Asks the backend to generate and save release artwork from the prompt. */
  onGenerate: () => void;
  /** Clears whatever artwork the release has. Never touches the song's. */
  onClear: () => void;
  /** True while a picked file is being read. */
  uploading?: boolean;
  /** Why the last file was rejected, when it was. */
  uploadError?: string | null;
  /** The uploaded file's name, so a wrong pick is noticeable before submission. */
  uploadedName?: string | null;
  /** True while generated artwork is being requested from the backend. */
  generating?: boolean;
  /** Why generated artwork could not be produced, when it could not. */
  generateError?: string | null;
}

export default function ArtworkStep({
  release,
  songs,
  source,
  onSourceChange,
  onReuseSong,
  onUpload,
  onArtworkPromptChange,
  onGenerate,
  onClear,
  uploading,
  uploadError,
  uploadedName,
  generating,
  generateError,
}: ArtworkStepProps) {
  const hasArtwork = Boolean(release.artworkUrl || release.artworkFromSongId);
  /*
    NO ARTWORK TO REUSE BECAUSE NO MUSIC IS CHOSEN — as opposed to because the chosen music
    has not resolved. `songs` is `releaseSongs` in the route: the release's ids mapped
    against the library with every miss dropped, so it is empty in two unrelated situations
    and the pane below told the user about the wrong one. A release that HOLDS ids and
    resolves none of them was being sent back to step 2 to pick songs it already has.

    Decided from `release.songIds` rather than from a read state, which is why this stays a
    presentational component: the release is the one thing that knows how many tracks were
    selected, and it is already a prop. Same shape as routes/Playlist.tsx's `unresolved`.
  */
  const noMusicChosen = release.songIds.length === 0;

  function take(list: FileList | null) {
    const file = firstImageFrom(list);
    if (file) onUpload(file);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ---- the preview ----
          ReleaseArtwork, the same component the dashboard row and the review step use, so
          what is shown here is exactly what will be shown there. Nothing else draws
          release artwork. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <ReleaseArtwork
          release={release}
          songs={songs}
          height={120}
          radius={10}
          style={{ width: 120, flexShrink: 0, overflow: 'hidden' }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: '0 0 4px', fontSize: 12.5, color: c.text1, fontWeight: 500 }}>
            {release.artworkUrl
              ? release.artworkOrigin === 'generate'
                ? 'Generated image'
                : 'Uploaded image'
              : release.artworkFromSongId
                ? 'Reusing a song’s artwork'
                : 'Placeholder gradient'}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: 11.5,
              color: c.text2,
              lineHeight: 1.55,
              overflowWrap: 'anywhere',
            }}
          >
            {/* THE HONEST CAPTION. A gradient is not an image file, so saying so here is
                what keeps the review step's artwork row from reading as "done". The same
                fact appears in the submission plan, where the artwork upload step is
                marked unresolved. */}
            {release.artworkUrl
              ? release.artworkOrigin === 'generate'
                ? 'This generated image is saved on the release and will be used as the submitted cover art.'
                : (uploadedName ?? 'This image will be uploaded as the release cover.')
              : release.artworkFromSongId
                ? 'The song’s artwork stands in here. If that artwork is itself a placeholder gradient, there is still no image file to deliver.'
                : 'A gradient generated from the release’s ID. It identifies the release in this app, but it is not an image file and cannot be delivered as cover art.'}
          </p>
          {hasArtwork && (
            <Btn variant="secondary" onClick={onClear} style={{ marginTop: 10 }}>
              <Trash2 size={13} />
              Remove
            </Btn>
          )}
        </div>
      </div>

      <RadioCardGroup label="Cover art source">
        <RadioCard
          name="publisher-artwork-source"
          value="song"
          checked={source === 'song'}
          onSelect={() => onSourceChange('song')}
          size="compact"
          ariaLabel="Use artwork from a song on this release"
        >
          <span style={{ fontSize: 12.5, color: c.text1 }}>Use a song’s artwork</span>
        </RadioCard>
        <RadioCard
          name="publisher-artwork-source"
          value="upload"
          checked={source === 'upload'}
          onSelect={() => onSourceChange('upload')}
          size="compact"
          ariaLabel="Upload a cover image"
        >
          <span style={{ fontSize: 12.5, color: c.text1 }}>Upload an image</span>
        </RadioCard>
        <RadioCard
          name="publisher-artwork-source"
          value="generate"
          checked={source === 'generate'}
          onSelect={() => onSourceChange('generate')}
          size="compact"
          ariaLabel="Generate cover art"
        >
          <span style={{ fontSize: 12.5, color: c.text1 }}>Generate artwork</span>
        </RadioCard>
      </RadioCardGroup>

      {/* ---- reuse a song's artwork ---- */}
      {source === 'song' &&
        (songs.length === 0 ? (
          <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
            {noMusicChosen
              ? 'Choose the music for this release in step 2 first — there is no song artwork to reuse yet.'
              : // The tracks are on the release; the library they live in has not arrived. Sending
                // the user to step 2 would be wrong advice, and step 2 itself now says which it is.
                'This release’s tracks could not be loaded, so their artwork cannot be shown. Upload an image instead, or reload and come back.'}
          </p>
        ) : (
          <div className="lyx-pick-list" role="group" aria-label="Song artwork">
            {songs.map((song) => {
              const chosen = release.artworkFromSongId === song.id;
              return (
                <label
                  key={song.id}
                  className="lyx-pick"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    border: `1px solid ${chosen ? c.accentBorder : c.border}`,
                    background: c.surface2,
                    borderRadius: 9,
                    padding: '9px 11px',
                    cursor: 'pointer',
                  }}
                >
                  {/* A radio, not a checkbox: a release has one cover. */}
                  <input
                    type="radio"
                    name="publisher-artwork-song"
                    className="lyx-visually-hidden"
                    checked={chosen}
                    onChange={() => onReuseSong(song.id)}
                    aria-label={`Use the artwork from ${song.title}`}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: '50%',
                      border: `1.5px solid ${chosen ? c.accentFill : c.borderStrong}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {chosen && (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: c.accentFill,
                        }}
                      />
                    )}
                  </span>
                  <SongArtwork
                    song={song}
                    height={34}
                    radius={6}
                    style={{ width: 34, flexShrink: 0, overflow: 'hidden' }}
                  />
                  <span style={{ minWidth: 0, flex: 1, fontSize: 12.5, color: c.text1 }}>
                    {song.title}
                  </span>
                </label>
              );
            })}
          </div>
        ))}

      {/* ---- upload ----
          THE ONE PATH ON THIS SCREEN THAT PRODUCES A REAL IMAGE, because the pixels are
          the user's own. It is why the artwork blocker in the submission plan clears when
          a file is uploaded and not otherwise. */}
      {source === 'upload' && (
        <div>
          <label
            className="lyx-cover-preview"
            onDragOver={(e) => {
              // Required, or the browser navigates to the dropped file instead of
              // handing it over.
              e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (!uploading) take(e.dataTransfer?.files ?? null);
            }}
            style={{
              background: c.surface2,
              border: `1px dashed ${c.borderStrong}`,
              cursor: uploading ? 'default' : 'pointer',
              maxWidth: 260,
            }}
          >
            <input
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              disabled={uploading}
              className="lyx-visually-hidden"
              aria-label={release.artworkUrl ? 'Replace the cover image' : 'Upload a cover image'}
              onChange={(e) => {
                take(e.target.files);
                // Cleared so picking the same file twice fires onChange the second time.
                e.target.value = '';
              }}
            />
            {uploading ? (
              <div className="lyx-cover-preview-empty">
                <>
                  <Loader2 size={18} color={c.accent} className="lyx-spin" />
                  <p style={{ fontSize: 12, color: c.text2, margin: '10px 0 0' }}>
                    Reading that image…
                  </p>
                </>
              </div>
            ) : release.artworkUrl ? (
              <>
                <img
                  src={release.artworkUrl}
                  alt={`Uploaded cover preview for ${release.title || 'this release'}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <span className="lyx-cover-hint">
                  <Upload size={12} aria-hidden="true" />
                  Click or drop to replace
                </span>
              </>
            ) : (
              <div className="lyx-cover-preview-empty">
                <>
                  <ImageIcon size={20} color={c.text2} aria-hidden="true" />
                  <p
                    style={{
                      fontSize: 12.5,
                      color: c.text1,
                      margin: '10px 0 0',
                      fontWeight: 500,
                    }}
                  >
                    <Upload size={11} style={{ marginRight: 5 }} />
                    Click or drop an image
                  </p>
                  <p style={{ fontSize: 11.5, color: c.text2, margin: '3px 0 0', lineHeight: 1.5 }}>
                    {ACCEPTED_IMAGE_LABEL} · up to {MAX_IMAGE_LABEL}
                  </p>
                </>
              </div>
            )}
          </label>

          {/* The rejection message, outside the label so it is not part of the target. */}
          {uploadError && (
            <p
              style={{
                margin: '10px 0 0',
                fontSize: 11.5,
                color: c.danger,
                lineHeight: 1.55,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 6,
              }}
            >
              <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
              {uploadError}
            </p>
          )}

          <p style={{ margin: '10px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
            {/* Stated because it is true and because it is the kind of rule a UI usually
                invents. LabelGrid's own artwork requirements are not in the committed
                spec, so this app does not assert any beyond format and size. */}
            Lyrixsa checks the format and the file size only. The distributor’s own artwork
            requirements are applied when the release is validated against it.
          </p>
        </div>
      )}

      {/* ---- generate ----
          This path now asks the backend for a real generated image and persists the prompt on
          the release draft, so the panel can behave like a real editor instead of a placeholder
          notice. */}
      {source === 'generate' && (
        <div
          style={{
            border: `1px solid ${c.border}`,
            background: c.surface2,
            borderRadius: 10,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
          }}
        >
          <ImagePlus size={15} color={c.accent} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: '0 0 6px', fontSize: 12.5, color: c.text1, fontWeight: 500 }}>
              Generate release artwork
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
              Describe the cover you want and Lyrixsa will ask the configured Google image model for
              a square release image, then save the returned artwork on this draft.
            </p>
            <TextField
              label="Prompt"
              value={release.artworkPrompt ?? ''}
              onChange={onArtworkPromptChange}
              multiline
              rows={3}
              placeholder="e.g. moody neon skyline, cinematic lighting, vinyl sleeve composition"
              marginBottom={10}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <Btn
                onClick={onGenerate}
                disabled={generating || !(release.artworkPrompt ?? '').trim()}
              >
                {generating ? <Loader2 size={13} className="lyx-spin" /> : <ImagePlus size={13} />}
                {generating ? 'Generating artwork…' : 'Generate artwork'}
              </Btn>
              {release.artworkUrl && release.artworkOrigin === 'generate' && (
                <span style={{ fontSize: 11.5, color: c.text2 }}>
                  The latest generated image is saved on this draft.
                </span>
              )}
            </div>
            {generateError && (
              <p
                style={{
                  margin: '10px 0 0',
                  fontSize: 11.5,
                  color: c.danger,
                  lineHeight: 1.55,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                }}
              >
                <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                {generateError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
